import {
  faComment,
  faEye,
  faImage,
  faTag,
  faHeart,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React from "react";
import { Link } from "react-router-dom";
import { isG80Car } from "../constants/vehicleConstants";
import PlateDisplay from "./PlateDisplay";

// Function to get pastel border color based on score
const getPastelBorderColor = (score) => {
  if (score < 30) return "border-r-red-200 dark:border-r-red-900";
  if (score < 60) return "border-r-yellow-200 dark:border-r-yellow-900";
  if (score < 80) return "border-r-green-200 dark:border-r-green-900";
  return "border-r-blue-200 dark:border-r-blue-900";
};

const VehicleLink = ({ vehicle, variant = "compact", onClick }) => {
  const isVideo =
    vehicle.images?.[0]?.url &&
    /\.(mp4|webm|mov|avi)/i.test(vehicle.images[0].url);
  const hasG80Badge =
    vehicle.basicInfo &&
    isG80Car(
      vehicle.basicInfo.make,
      vehicle.basicInfo.model,
      vehicle.g80ConversionKit === "Yes"
    );
  const hasG80Kit = vehicle.g80ConversionKit === "Yes";
  const tags = vehicle.tags || [];

  // Get the border color based on profile richness score
  const borderColorClass = getPastelBorderColor(
    vehicle.profileRichnessScore || 0
  );

  if (variant === "card") {
    return (
      <Link
        to={`/plate/${vehicle.plate}`}
        className={`bg-white dark:bg-dark-surface rounded-lg shadow-sm hover:shadow-md transition-all flex flex-col border border-gray-100 dark:border-gray-700 ${borderColorClass} border-r-4 group relative overflow-hidden`}
        onClick={onClick}
      >
        <div className="h-40 relative">
          {/* Image count pill */}
          {vehicle.images && vehicle.images.length > 0 && (
            <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
              <FontAwesomeIcon icon={faImage} className="w-3 h-3" />
              <span className="text-white">{vehicle.images.length}</span>
            </div>
          )}

          {/* Tags in top left */}
          {tags.length > 0 && (
            <div className="absolute top-2 left-2 flex flex-wrap gap-1">
              {tags.map((tag) => (
                <span
                  key={tag.type}
                  className="bg-black/50 text-white text-[10px] px-2 py-0.5 rounded-full"
                >
                  {tag.type}
                </span>
              ))}
            </div>
          )}

          {vehicle.images && vehicle.images.length > 0 ? (
            isVideo ? (
              <video
                src={vehicle.images[0].url}
                className="w-full h-full object-cover"
                muted
                playsInline
                loop
                autoPlay
              />
            ) : (
              <picture>
                {vehicle.images[0].variants?.webp?.medium && (
                  <source
                    srcSet={vehicle.images[0].variants.webp.medium}
                    type="image/webp"
                  />
                )}
                <img
                  src={
                    vehicle.images[0].variants?.medium || vehicle.images[0].url
                  }
                  alt={vehicle.plate}
                  className="w-full h-full object-cover"
                />
              </picture>
            )
          ) : (
            <div className="w-full h-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faImage}
                className="text-gray-300 dark:text-gray-600 text-4xl"
              />
            </div>
          )}

          {(hasG80Badge || hasG80Kit) && (
            <span
              className={`absolute bottom-2 left-2 text-center text-xs font-medium py-0.5 px-2 rounded ${
                hasG80Badge
                  ? "bg-green-100/90 text-green-800"
                  : "bg-yellow-100/90 text-yellow-800"
              }`}
            >
              {hasG80Badge ? "G80" : "G80 Kit"}
            </span>
          )}
        </div>

        <div className="p-4 flex-1 flex flex-col bg-white dark:bg-dark-surface">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex-shrink-0 max-w-[70%]">
              <PlateDisplay
                plate={vehicle.plate}
                plateStyle={vehicle.plateStyle}
                size="default"
              />
            </div>
            {vehicle.basicInfo && (
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                {vehicle.basicInfo.make} {vehicle.basicInfo.model}
              </span>
            )}
          </div>

          {vehicle.profile && (
            <p className="text-xs text-gray-400 line-clamp-2 bg-gray-50 rounded p-2 mb-auto dark:bg-gray-800">
              {vehicle.profile}
            </p>
          )}

          <div className="flex justify-between items-center mt-3">
            <div className="flex items-center gap-3 text-[10px] text-gray-500">
              <div className="flex items-center gap-0.5">
                <FontAwesomeIcon icon={faEye} className="w-2.5 h-2.5" />
                <span>{vehicle.hits ?? 0}</span>
              </div>
              {vehicle.likesCount > 0 && (
                <div className="flex items-center gap-0.5 text-red-500">
                  <FontAwesomeIcon icon={faHeart} className="w-2.5 h-2.5" />
                  <span>{vehicle.likesCount}</span>
                </div>
              )}
              {vehicle.discussionCount > 0 && (
                <div className="flex items-center gap-0.5 text-blue-600">
                  <FontAwesomeIcon icon={faComment} className="w-2.5 h-2.5" />
                  <span>{vehicle.discussionCount}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/plate/${vehicle.plate}`}
      className={`bg-white dark:bg-dark-surface rounded-lg shadow-sm hover:shadow-md transition-all flex items-stretch group border border-gray-100 dark:border-gray-700 ${borderColorClass} border-r-4 relative overflow-hidden h-[110px] my-3`}
      onClick={onClick}
    >
      {/* Image Section with Overlays */}
      <div className="w-[130px] relative flex-shrink-0">
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent z-10" />
        {vehicle.images && vehicle.images.length > 0 ? (
          isVideo ? (
            <video
              src={vehicle.images[0].url}
              className="w-full h-full object-cover"
              muted
              playsInline
              loop
              autoPlay
            />
          ) : (
            <picture>
              {vehicle.images[0].variants?.webp?.thumbnail && (
                <source
                  srcSet={vehicle.images[0].variants.webp.thumbnail}
                  type="image/webp"
                />
              )}
              <img
                src={
                  vehicle.images[0].variants?.thumbnail || vehicle.images[0].url
                }
                alt={vehicle.plate}
                className="w-full h-full object-cover"
              />
            </picture>
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-800">
            <FontAwesomeIcon
              icon={faImage}
              className="text-gray-300 dark:text-gray-600 text-3xl"
            />
          </div>
        )}

        {/* Overlaid Content */}
        <div className="absolute inset-0 z-20 p-1.5 flex flex-col justify-between">
          {/* Top Row */}
          <div className="flex justify-between items-start">
            {/* Tags */}
            <div className="flex flex-wrap gap-1 max-w-[70%]">
              {tags.length > 0 && (
                <span
                  key={tags[0].type}
                  className="bg-black/60 backdrop-blur-sm text-white text-[9px] px-1.5 py-0.5 rounded-full"
                >
                  {tags[0].type}
                  {tags.length > 1 && ` +${tags.length - 1}`}
                </span>
              )}
            </div>

            {/* G80 Badge */}
            {(hasG80Badge || hasG80Kit) && (
              <span
                className={`text-[9px] font-medium px-2 py-0.5 text-center rounded-sm w-[40px] ${
                  hasG80Badge
                    ? "bg-green-500/90 text-white"
                    : "bg-yellow-500/90 text-white"
                }`}
              >
                {hasG80Badge ? "G80" : "G80 Kit"}
              </span>
            )}
          </div>

          {/* Bottom Row */}
          <div className="flex justify-between items-end">
            {/* Media Count */}
            {vehicle.images && vehicle.images.length > 0 && (
              <div className="flex items-center gap-0.5 bg-black/60 backdrop-blur-sm text-white text-[9px] rounded-full px-1.5 py-0.5">
                <FontAwesomeIcon
                  icon={faImage}
                  className="w-2.5 h-2.5 text-white"
                />
                <span className="text-white">{vehicle.images.length}</span>
              </div>
            )}

            {/* Stats */}
            <div className="flex items-center gap-1 text-[9px] text-white">
              <div className="flex items-center gap-0.5">
                <FontAwesomeIcon
                  icon={faEye}
                  className="w-2.5 h-2.5 text-white"
                />
                <span className="text-white">{vehicle.hits ?? 0}</span>
              </div>
              {vehicle.likesCount > 0 && (
                <div className="flex items-center gap-0.5">
                  <FontAwesomeIcon
                    icon={faHeart}
                    className="w-2.5 h-2.5 text-red-400"
                  />
                  <span className="text-red-400">{vehicle.likesCount}</span>
                </div>
              )}
              {vehicle.discussionCount > 0 && (
                <div className="flex items-center gap-0.5">
                  <FontAwesomeIcon
                    icon={faComment}
                    className="w-2.5 h-2.5 text-green-400"
                  />
                  <span className="text-green-400">
                    {vehicle.discussionCount}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-grow min-w-0 p-2">
        <div className="">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-shrink-0 max-w-[70%]">
              <PlateDisplay
                plate={vehicle.plate}
                plateStyle={vehicle.plateStyle}
                size="small"
              />
            </div>
            {vehicle.basicInfo && (
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">
                {vehicle.basicInfo.make} {vehicle.basicInfo.model}
              </span>
            )}
          </div>
        </div>

        {vehicle.profile && (
          <p className="text-xs text-gray-400 dark:text-gray-400 line-clamp-2 bg-gray-50/80 dark:bg-gray-700/50 rounded p-1.5 mb-1">
            {vehicle.profile}
          </p>
        )}
      </div>
    </Link>
  );
};

export default VehicleLink;
